import { componentWrapper } from "../../utilities";
import type {
    CustomAutoMenuFactory,
    IAutoMenuElement,
    IAutoMenuItem,
    IAutoMenuWithPlainItemsProps,
    IProps,
} from "./auto-menu.types";
import { Menu } from "../menu";
import { cloneElement, isValidElement, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Icon } from "../icon";
import { Text } from "../text";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { kebabCase } from "lodash";
import { Highlight } from "../highlight";
import { Group } from "../group";
import { ScrollArea } from "../scroll-area";

/**
 * AutoMenu component for automatically generating menu structures from item arrays.
 * @param {React.ReactNode} props.children - The trigger element that opens the menu.
 * @param {Array} props.items - Array of menu items to display.
 * @param {string} props.title - Optional title to display in the menu.
 * @param {string} props.subtitle - Optional subtitle to display in the menu.
 * @param {function} props.onClick - Callback function when a menu item is clicked.
 * @param {boolean} props.searchable - Whether the menu should have a search input.
 * @param {function} props.onChange - Callback function when the menu state changes.
 * @param {boolean} props.hasCustomSearch - Whether the menu uses a custom search implementation.
 * @param {string} props.noItemsFoundLabel - Label to show when no items match the search.
 * @param {string | number} props.maxMenuHeight - Maximum height for the menu.
 * @returns {JSX.Element} The AutoMenu component.
 */
const AutoMenu = componentWrapper<CustomAutoMenuFactory, IProps>(
    "Menu",
    (
        {
            children,
            items,
            title,
            subtitle,
            onClick,
            searchable,
            onChange,
            hasCustomSearch,
            noItemsFoundLabel,
            "data-testid": dataTestId,
            groupType = "sub-menu",
            highlightedText,
            maxMenuHeight,
            ...other
        },
        ref
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const [search, setSearch] = useState("");

        const internalHighlightedText = useMemo(() => {
            return highlightedText || search || "";
        }, [search, highlightedText]);

        const usedGroupType = useMemo(() => {
            return search.length > 0 ? "label" : groupType;
        }, [search.length, groupType]);

        const buildMenu = useCallback(
            (item: IAutoMenuElement, key: string, index?: number): ReactNode => {
                if (isValidElement(item)) {
                    return cloneElement(item, { key });
                }
                if ("label" in item) {
                    return <Menu.Label key={item.label}>{item.label}</Menu.Label>;
                }
                if ("items" in item && typeof item.items !== "undefined") {
                    if (usedGroupType === "label") {
                        const filteredSubitems =
                            searchable && search.length > 0
                                ? item.items.filter((subItem) =>
                                      subItem.text.toLowerCase().includes(search.toLowerCase())
                                  )
                                : item.items;
                        if (filteredSubitems.length === 0) {
                            return null;
                        }
                        const children = [
                            <Menu.Label key={item.text}>
                                <Group gap="xs">
                                    {item.icon && <Icon icon={item.icon} />}
                                    {item.text}
                                </Group>
                            </Menu.Label>,
                            ...filteredSubitems.map((subItem, index) => {
                                return (
                                    <Menu.Item
                                        onClick={(e) => {
                                            onClick?.(subItem.value ?? subItem.text);
                                            subItem.menuItemProps?.onClick?.(e);
                                        }}
                                        value={subItem.value ?? subItem.text}
                                        active={subItem.active}
                                        data-testid={`menu-item-${kebabCase(subItem.text)}`}
                                        key={`${key}-${index}`}
                                        {...subItem.menuItemProps}
                                    >
                                        <Highlight size="sm" color="blue" highlight={internalHighlightedText}>
                                            {subItem.text}
                                        </Highlight>
                                    </Menu.Item>
                                );
                            }),
                        ];
                        // Add a horizontal line after the group unless it's the last group
                        const lastGroupIndex = (() => {
                            for (let i = items.length - 1; i >= 0; i--) {
                                const maybeGroup = items[i];
                                if (
                                    !isValidElement(maybeGroup) &&
                                    "items" in maybeGroup &&
                                    typeof maybeGroup.items !== "undefined"
                                )
                                    return i;
                            }
                            return -1;
                        })();
                        if (typeof index === "number" && index !== lastGroupIndex) {
                            children.push(<Menu.Divider key={`${key}-divider`} />);
                        }
                        return children;
                    }
                    if (usedGroupType === "line") {
                        const children = item.items.map((subItem, index) => {
                            return (
                                <Menu.Item
                                    onClick={(e) => {
                                        onClick?.(subItem.value ?? subItem.text);
                                        subItem.menuItemProps?.onClick?.(e);
                                    }}
                                    value={subItem.value ?? subItem.text}
                                    active={subItem.active}
                                    data-testid={`menu-item-${kebabCase(subItem.text)}`}
                                    key={`${key}-${index}`}
                                    {...subItem.menuItemProps}
                                >
                                    <Highlight size="sm" color="blue" highlight={internalHighlightedText}>
                                        {subItem.text}
                                    </Highlight>
                                </Menu.Item>
                            );
                        });
                        // Add a horizontal line after the group unless it's the last group
                        const lastGroupIndex = (() => {
                            for (let i = items.length - 1; i >= 0; i--) {
                                const maybeGroup = items[i];
                                if (
                                    !isValidElement(maybeGroup) &&
                                    "items" in maybeGroup &&
                                    typeof maybeGroup.items !== "undefined"
                                )
                                    return i;
                            }
                            return -1;
                        })();
                        if (typeof index === "number" && index !== lastGroupIndex) {
                            children.push(<Menu.Divider key={`${key}-divider`} />);
                        }
                        return children;
                    }
                    const buildedItems = item.items
                        .map((subItem, index) => {
                            return buildMenu(subItem, `subItem-${index}`);
                        })
                        .filter((subItem) => subItem !== null);
                    return buildedItems.length > 0 ? (
                        <Menu.Sub key={key}>
                            <Menu.Sub.Target>
                                <Menu.Sub.Item
                                    leftSection={item.icon && <Icon icon={item.icon} />}
                                    disabled={item.disabled}
                                    data-testid={`menu-sub-item-${kebabCase(item.text)}`}
                                    active={item.active}
                                >
                                    <Highlight size="sm" color="blue" highlight={internalHighlightedText}>
                                        {item.text}
                                    </Highlight>
                                </Menu.Sub.Item>
                            </Menu.Sub.Target>
                            <Menu.Sub.Dropdown data-testid={`menu-sub-dropdown-${kebabCase(item.text)}`}>
                                {buildedItems}
                            </Menu.Sub.Dropdown>
                        </Menu.Sub>
                    ) : null;
                } else if ("text" in item) {
                    return item.text.toLowerCase().includes(search.toLowerCase()) ? (
                        <Menu.Item
                            key={key}
                            onClick={(e) => {
                                onClick?.(item.value ?? item.text);
                                item.menuItemProps?.onClick?.(e);
                            }}
                            value={item.value ?? item.text}
                            leftSection={item.icon && <Icon icon={item.icon} />}
                            active={item.active}
                            data-testid={`menu-item-${kebabCase(item.text)}`}
                            {...item.menuItemProps}
                        >
                            <Highlight size="sm" color="blue" highlight={internalHighlightedText}>
                                {item.text}
                            </Highlight>
                        </Menu.Item>
                    ) : null;
                }
            },
            [onClick, search, groupType, items]
        );

        const builtMenuItems = useMemo(() => {
            return items.map((item, key) => buildMenu(item, `item-${key}`, key));
        }, [items, buildMenu]);

        const hasVisibleItems = builtMenuItems.some((item) => item !== null);

        return (
            <Menu
                onExitTransitionEnd={() => {
                    inputRef.current?.blur(); // Blur the input when the menu closes
                }}
                onEnterTransitionEnd={() => {
                    inputRef.current?.focus(); // Focus the input when the menu opens
                }}
                onClose={() => {
                    onChange?.(false); // call the onChange prop to close the menu when clicking outside of the menu.
                    setSearch(""); // Clear search on close
                }}
                {...other}
            >
                <Menu.Target>{children}</Menu.Target>
                <Menu.Dropdown ref={ref} data-testid={dataTestId ?? "auto-menu-dropdown"}>
                    {searchable && (
                        <Menu.Search
                            inputProps={{
                                ref: inputRef,
                                placeholder: "Search filter",
                                onChange: (event) => setSearch(event.currentTarget.value),
                            }}
                        />
                    )}
                    {(title || subtitle) && <Menu.Info title={title} subtitle={subtitle} />}
                    {typeof maxMenuHeight !== "undefined" ? (
                        <ScrollArea.Autosize mah={maxMenuHeight}>{builtMenuItems}</ScrollArea.Autosize>
                    ) : (
                        builtMenuItems
                    )}
                    {!hasVisibleItems && (searchable || hasCustomSearch) && (
                        <Text size="sm" dimmed ta="center" fs="italic" py="lg">
                            {typeof noItemsFoundLabel === "undefined" ? "No items found" : noItemsFoundLabel}
                        </Text>
                    )}
                </Menu.Dropdown>
            </Menu>
        );
    }
);

/**
 * AutoMenu component that accepts plain items and transforms them using getter functions.
 * @template Item - The type of items in the array.
 * @param {Array<Item>} props.items - Array of plain items to transform into menu items.
 * @param {function} props.getSection - Function to get section label from an item.
 * @param {function} props.getLabel - Function to get label text from an item.
 * @param {function} props.getValue - Function to get value from an item.
 * @param {function} props.getGroup - Function to get group name from an item.
 * @param {function} props.getGroupOrder - Function to get group order from an item.
 * @param {function} props.getGroupItemOrder - Function to get item order within a group.
 * @param {function} props.getIcon - Function to get icon from an item.
 * @param {function} props.getItemOrder - Function to get item order.
 * @param {function} props.getGroupIcon - Function to get icon for a group.
 * @param {function} props.getActive - Function to determine if an item is active.
 * @param {boolean} props.moveActiveItemToTop - Whether to move active items to the top of the list.
 * @param {string | number} props.maxMenuHeight - Maximum height for the menu.
 * @returns {JSX.Element} The AutoMenuWithPlainItems component.
 */
export const AutoMenuWithPlainItems = <Item,>({
    items,
    getSection,
    getLabel,
    getValue,
    getGroup,
    getGroupOrder,
    getGroupItemOrder,
    getIcon,
    getItemOrder,
    getGroupIcon,
    getActive,
    moveActiveItemToTop = false,
    ...other
}: IAutoMenuWithPlainItemsProps<Item>) => {
    const transformedItems = useMemo<Array<IAutoMenuElement>>(() => {
        if (!items || items.length === 0) {
            return [];
        }

        // Transform the list of Items into a list of our custom object with the props:
        // text, icon, itemOrder, group, groupOrder, groupItemOrder
        const transformedItems: Array<{
            text: string;
            value: string | number;
            icon?: IconDefinition;
            itemOrder?: number;
            group?: string;
            groupOrder?: number;
            groupItemOrder?: number;
            active?: boolean;
        }> = items.map((item) => {
            return {
                active: getActive?.(item),
                text: getLabel(item),
                value: getValue(item),
                icon: getIcon?.(item),
                itemOrder: getItemOrder?.(item),
                group: getGroup?.(item),
                groupOrder: getGroupOrder?.(item),
                groupItemOrder: getGroupItemOrder?.(item),
            };
        });

        // Reorder every item, using the orders, and remove tthe props:
        // itemOrder, groupOrder and groupItemOrder from each item (asuming they will be already ordered)
        const orderedTransformedItems: Array<{
            text: string;
            value: string | number;
            icon?: IconDefinition;
            itemOrder?: number;
            group?: string;
            groupOrder?: number;
            groupItemOrder?: number;
            active?: boolean;
        }> = transformedItems
            .sort((itemOne, itemTwo) => itemOne.text.localeCompare(itemTwo.text))
            .sort((itemOne, itemTwo) =>
                typeof itemOne.itemOrder !== "undefined" && typeof itemTwo.itemOrder !== "undefined"
                    ? itemOne.itemOrder - itemTwo.itemOrder
                    : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group if any
                typeof itemOne.group !== "undefined" && typeof itemTwo.group !== "undefined"
                    ? itemOne.group.localeCompare(itemTwo.group)
                    : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group order if exists
                typeof itemOne.groupOrder !== "undefined" && typeof itemTwo.groupOrder !== "undefined"
                    ? itemOne.groupOrder - itemTwo.groupOrder
                    : 0
            )
            .sort((itemOne, itemTwo) => {
                if (typeof itemOne.group !== "undefined" && itemOne.group === itemTwo.group) {
                    // Sort by group item order if exists
                    return typeof itemOne.groupItemOrder !== "undefined" &&
                        typeof itemTwo.groupItemOrder !== "undefined"
                        ? itemOne.groupItemOrder - itemTwo.groupItemOrder
                        : 0;
                }
                return 0;
            })
            .map(({ itemOrder, groupOrder, groupItemOrder, ...rest }) => {
                return rest;
            });

        // Group the items by group, using the group prop, and remove the group prop from each item
        const groupedItems: Array<IAutoMenuElement> = orderedTransformedItems.reduce((acc, item) => {
            const { group, ...itemWithoutGroup } = item;
            if (group) {
                const existingGroup: IAutoMenuItem | undefined = acc.find(
                    (accItem) => "text" in accItem && "items" in accItem && accItem.text === group
                ) as IAutoMenuItem;

                if (existingGroup) {
                    if (existingGroup.items) {
                        existingGroup.items.push(itemWithoutGroup);
                        // If this child item is active, mark the parent group as active
                        if (item.active && !existingGroup.active && !moveActiveItemToTop) {
                            existingGroup.active = true;
                        }
                    } else {
                        existingGroup.items = [itemWithoutGroup];
                        if (item.active && !moveActiveItemToTop) {
                            existingGroup.active = true;
                        }
                    }
                } else {
                    acc.push({
                        text: group,
                        value: item.value,
                        icon: getGroupIcon?.(group),
                        items: [itemWithoutGroup],
                        ...(item.active && !moveActiveItemToTop && { active: true }),
                    });
                }
            } else {
                acc.push({
                    text: item.text,
                    value: item.value,
                    icon: item.icon,
                    ...(item.active && { active: item.active }),
                } as IAutoMenuItem);
            }
            return acc;
        }, [] as Array<IAutoMenuElement>);

        // Move the active item to the top of the list
        if (moveActiveItemToTop) {
            let activeItem: IAutoMenuItem | null = null;
            const modifiedGroups: Array<IAutoMenuElement> = [];

            for (const item of groupedItems) {
                if ("items" in item && item.items) {
                    const activeItemInGroup = item.items.find((subItem) => subItem.active);

                    // If there is an active item in the group, create it as an item without the group so it can be displayed at the top of the list
                    if (activeItemInGroup && !activeItem) {
                        activeItem = {
                            text: activeItemInGroup.text,
                            value: activeItemInGroup.value,
                            icon: activeItemInGroup.icon,
                            active: true,
                        };

                        // Remove the active item from the parent group's items
                        const remainingItems = item.items.filter(
                            (subItem) => subItem.value !== activeItemInGroup.value || !subItem.active
                        );

                        // Only add the parent group if it has remaining items
                        if (remainingItems.length > 0) {
                            modifiedGroups.push({
                                text: item.text,
                                value: item.value,
                                icon: item.icon,
                                items: remainingItems,
                                active: false,
                            });
                        }
                    } else {
                        modifiedGroups.push(item);
                    }
                } else if ("active" in item && item.active && !activeItem) {
                    activeItem = item;
                } else {
                    modifiedGroups.push(item);
                }
            }

            return activeItem ? [activeItem, ...modifiedGroups] : modifiedGroups;
        }

        return groupedItems;
    }, [
        items,
        moveActiveItemToTop,
        getActive,
        getLabel,
        getValue,
        getIcon,
        getItemOrder,
        getGroup,
        getGroupOrder,
        getGroupItemOrder,
        getGroupIcon,
    ]);

    return <AutoMenu items={transformedItems} {...other} />;
};

AutoMenu.WithPlainItems = AutoMenuWithPlainItems;

export { AutoMenu };
